import { useEffect, useMemo, useState } from "react";
import { Dropdown, Form, Modal, Placeholder } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import translate from "../../../../services/Translate";
import { PermissionsApiKeyCreationModalProps, Role } from "./CreationModal.d";
import { createPermissionsApiKey, getRoles } from "./CreationModal.function";

const PermissionsApiKeyCreationModal = ({ closeModal, language, show }: PermissionsApiKeyCreationModalProps) => {
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [currentRole, setCurrentRole] = useState<number>(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getRoles(setRoles, language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    const optionalFields = {
      description: description || null,
      dt_expiration: expirationDate || null,
    };

    const payload = {
      name,
      role: currentRole,
      ...optionalFields,
    };

    createPermissionsApiKey(payload, language, setLoading, () => {
      resetSelection();
      closeModal();
    });
  };

  const resetSelection = () => {
    setName("");
    setDescription("");
    setExpirationDate("");
    setRoles([]);
    setCurrentRole(1);
  };

  const isFormValid = useMemo(() => !!name && !!currentRole, [name, currentRole]);

  return (
    <Popup
      show={show}
      onClose={() => closeModal()}
      modalTitle={translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.TITLE")}
      scrollable
    >
      <Form id="modal-add-permissions-api-key">
        <Form.Group>
          <Form.Label>
            <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
          </Form.Label>
          <Form.Control
            className={`border-secondary ${!name ? "text-muted" : ""}`}
            type="text"
            placeholder={translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.PLACEHOLDERS.NAME")}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>{translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.LABELS.DESCRIPTION")}</Form.Label>
          <Form.Control
            className={`border-secondary ${!description ? "text-muted" : ""}`}
            as="textarea"
            rows={3}
            placeholder={translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.PLACEHOLDERS.DESCRIPTION")}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.LABELS.ROLE")} />
          </Form.Label>
          {roles.length > 0 ? (
            <Dropdown>
              <Dropdown.Toggle
                className="w-100 d-flex justify-content-between align-items-center border-secondary"
                variant=""
              >
                {currentRole
                  ? roles.find((defaultRole) => defaultRole.id === currentRole)?.name
                  : translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.PLACEHOLDERS.ROLE")}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                {roles.map((selectedRole) => (
                  <Dropdown.Item key={selectedRole.id} onClick={() => setCurrentRole(selectedRole.id)}>
                    {selectedRole.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <Placeholder animation="glow">
              <Placeholder.Button className="w-100" />
            </Placeholder>
          )}
        </Form.Group>

        <Form.Group className="mt-3">
          <Form.Label>{translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.LABELS.DT_EXPIRATION")}</Form.Label>
          <Form.Control
            className={`border-secondary ${!expirationDate ? "text-muted" : ""}`}
            type="datetime-local"
            value={expirationDate}
            onChange={(event) => setExpirationDate(event.target.value)}
          />
        </Form.Group>
      </Form>
      <Modal.Footer>
        <DstButton
          variant="secondary"
          value={translate(language, "GLOBAL.CANCEL")}
          clickFunction={() => {
            closeModal();
            resetSelection();
          }}
        />
        <DstButton
          value={translate(language, "PAGES.PERMISSIONS_API_KEY.MODAL.BUTTONS.CONFIRM")}
          clickFunction={handleConfirm}
          disabled={!isFormValid}
          loading={loading}
        />
      </Modal.Footer>
    </Popup>
  );
};

export default PermissionsApiKeyCreationModal;
